import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useLocation, useRouteMatch } from 'react-router-dom';
import { Spin } from 'antd';
import { encode, decode } from 'js-base64';
import queryString from 'query-string';
import { useQuery } from '@apollo/client';
import classnames from 'classnames';

import generatePath from '~settings/routing';
import authControl from '~components/auth-control';
import DetailLayout from '@pages/dashboard/landlord/details/detail-layout';
import * as landlordActions from '~actions/landlord';
import { landlordDetailTabs } from '~constants/landlord';
import { communicationStatus, platformEntity, entityAction, needShowTokenTab } from '~constants';
import LandlordHeader from '~pages/dashboard/landlord/details/header';
import LandlordBasicInfo from '~pages/dashboard/landlord/details/basic-info';
import LandlordContact from '~pages/dashboard/landlord/details/contact';
import LandlordContract from '~pages/dashboard/landlord/details/contract';
import LandlordVerification from '~pages/dashboard/landlord/details/verification';
import LandlordInVoiceInfo from '~pages/dashboard/landlord/details/invoice';
import PaymentDashboard from '~pages/dashboard/payment';
import LandlordPropertyRelated from '~pages/dashboard/landlord/details/property-related';
import LandlordBookings from '~pages/dashboard/landlord/details/bookings';
import LandlordToken from '~pages/dashboard/landlord/details/token';
import LandlordCommission from '~pages/dashboard/landlord/details/commission';
import { QUERY_LONGTAIL_PROPERTIES_TOTAL_COUNT } from './query';

const LandlordDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { landlordSlug } = useParams();

  const { data: longtailProperties } = useQuery(QUERY_LONGTAIL_PROPERTIES_TOTAL_COUNT, {
    variables: {
      landlordSlug: landlordSlug,
    },
    fetchPolicy: 'network-only',
  });

  const longtailPropertiesTotal = useMemo(
    () => longtailProperties?.properties?.totalCount,
    [longtailProperties],
  );

  const dispatch = useDispatch();
  const [roleId, setRoleId] = useState();
  const [apiTokens, setApiTokens] = useState();

  const currentRoleSlugRef = useRef(null);

  const search = queryString.parse(location?.search);

  const match = useRouteMatch('/landlord/:landlordSlug/payment/list');

  const [activeTab, setActiveTab] = useState(search?.tab || landlordDetailTabs.BASIC_INFO);
  const [landlordNameList, setLandlordNameList] = useState([]);

  const {
    landlord,
    getLandlordStatus,
    updateLandlordStatus,
    propertiesOfLandlord,
    propertiesOfLandlordCount,
    propertyListStatus,
    currentRoleSlug,
    accountInfo,
  } = useSelector(state => ({
    landlord: state.dashboard.landlord?.toJS().current,
    getLandlordStatus: state.dashboard.landlord?.toJS().communication.current.status,
    updateLandlordStatus: state.dashboard.landlord?.toJS().communication.update.status,
    propertiesOfLandlord: state.dashboard.landlord?.toJS().properties.list,
    propertiesOfLandlordCount: state.dashboard.landlord?.toJS().properties.totalCount,
    propertyListStatus: state.dashboard.landlord?.toJS().communication.propertyList.status,
    currentRoleSlug: state.auth?.toJS().auth.currentRoleSlug,
    accountOwners: state.dashboard.landlord?.toJS().accountOwners,
    getAccountOwnersStatus: state.dashboard.landlord?.toJS().communication.getAccountOwners.status,
    accountInfo: state.dashboard.account?.toJS().account,
  }));

  const getLandlord = useCallback(
    (slug, successCallback) =>
      dispatch(landlordActions.getLandlord(slug, successCallback, successCallback)),
    [dispatch],
  );

  const setPreparedContract = useCallback(
    payload => dispatch(landlordActions.setPreparedContract(payload)),
    [dispatch],
  );

  const updateLandlord = useCallback(
    (params, successCallback, errorCallback) =>
      dispatch(landlordActions.updateLandlord(params, successCallback, errorCallback)),
    [dispatch],
  );

  const setCurrentLandlord = useCallback(
    landlord => dispatch(landlordActions.setCurrentLandlord(landlord)),
    [dispatch],
  );

  const searchLandlordList = useCallback(
    successCallback => dispatch(landlordActions.searchLandlordList(successCallback)),
    [dispatch],
  );

  const getPropertiesByLandlordSlug = useCallback(
    params => dispatch(landlordActions.getPropertiesByLandlordSlug(params)),
    [dispatch],
  );

  const setPropertiesOfLandlord = useCallback(
    properties => dispatch(landlordActions.setPropertiesOfLandlord(properties)),
    [dispatch],
  );

  const searchPropertyNameList = useCallback(
    (params, successCallback) =>
      dispatch(landlordActions.searchPropertyNameList(params, successCallback)),
    [dispatch],
  );

  const getDisplayCountryList = useCallback(
    successCallback => dispatch(landlordActions.getDisplayCountryList(successCallback)),
    [dispatch],
  );

  const createApiToken = useCallback(
    (params, successCallback) => dispatch(landlordActions.createAPIToken(params, successCallback)),
    [dispatch],
  );

  const updateApiToken = useCallback(
    (params, successCallback) => dispatch(landlordActions.updateApiToken(params, successCallback)),
    [dispatch],
  );

  const queryGetApiTokens = useCallback(
    (params, successCallback) => {
      if (needShowTokenTab.includes(currentRoleSlug)) {
        dispatch(landlordActions.queryGetApiTokens(params, successCallback));
      }
    },
    [dispatch],
  );

  const gotoCreateProperty = () => {
    const { name, slug, id, bookingJourney } = landlord;

    history.push(generatePath('property.create'), {
      landlord: {
        name,
        slug,
        id,
        bookingJourney,
      },
    });
  };

  const handleFetchLandlord = useCallback(() => {
    getLandlord(landlordSlug, landlord => {
      if (landlord) {
        console.log('landlord --->', landlord);
        setCurrentLandlord(landlord);
        setPropertiesOfLandlord(landlord);
      } else {
        history.push(generatePath('landlords', {}));
      }
    });
  }, [getLandlord, history, landlordSlug, setCurrentLandlord, setPropertiesOfLandlord]);

  const handleGetAPITokens = () => {
    const decodedValue = decode(landlord?.id);
    const roleId = encode(decodedValue?.replace('Landlord', 'RoleId'));
    setRoleId(roleId);
    queryGetApiTokens({ roleId }, res => {
      if (res.getApiTokens) {
        setApiTokens(res.getApiTokens.edges);
      }
    });
  };

  const handleChangeTab = tab => {
    const isSubMenu = [
      t('cms.landlord.verification.business_info'),
      t('cms.landlord.verification.vetting_documentation'),
      t('cms.landlord.verification.landlord_status'),
    ].includes(tab?.key);

    if (tab?.key === landlordDetailTabs.TOKEN) {
      handleGetAPITokens();
    }

    window.location.hash = isSubMenu ? `#${tab?.key}` : '';

    if (tab.key === landlordDetailTabs.PAYMENTPLAN) {
      history.replace(generatePath('paymentList', { landlordSlug: landlord?.slug }));
    } else {
      if (match?.isExact) {
        history.replace(generatePath('landlord', { landlordSlug: landlord?.slug }));
      }
    }
    setActiveTab(
      isSubMenu ? landlordDetailTabs.VERIFICATION : tab?.key ?? landlordDetailTabs.BASIC_INFO,
    );
  };

  useEffect(() => {
    if (
      currentRoleSlugRef.current !== 'content_manager_level_2' &&
      currentRoleSlug === 'content_manager_level_2'
    ) {
      setActiveTab(landlordDetailTabs.VERIFICATION);
    }

    currentRoleSlugRef.current = currentRoleSlug;
  }, [currentRoleSlug]);
  useEffect(() => {
    if (match?.isExact) {
      setActiveTab(landlordDetailTabs.PAYMENTPLAN);
    }
  }, [match]);
  useEffect(() => {
    handleFetchLandlord();

    searchLandlordList(res => {
      setLandlordNameList(res.search.edges.map(landlord => landlord.node));
    });
  }, [handleFetchLandlord, searchLandlordList]);

  useEffect(() => {
    if (landlord?.id) {
      handleGetAPITokens();
    }
  }, [landlord?.id]);

  const updateLandlordLoading = updateLandlordStatus === communicationStatus.FETCHING;

  return (
    <div className="landlord-details">
      <Choose>
        <When condition={getLandlordStatus !== communicationStatus.IDLE}>
          <div className="landlord-details__loading">
            <Spin />
          </div>
        </When>
        <When condition={landlord}>
          <LandlordHeader landlord={landlord} getLandlord={handleFetchLandlord} />
          <DetailLayout
            activeTab={activeTab}
            handleSetActiveTab={handleChangeTab}
            longtailPropertiesTotal={longtailPropertiesTotal}
          >
            <div
              className={classnames(
                'landlord-details__wrapper',
                [landlordDetailTabs.TOKEN, landlordDetailTabs.PAYMENTPLAN].includes(activeTab) &&
                  'landlord-details__wrapper-nopadding',
              )}
            >
              <Choose>
                <When condition={activeTab === landlordDetailTabs.BASIC_INFO}>
                  <LandlordBasicInfo
                    t={t}
                    landlord={landlord}
                    setPreparedContract={setPreparedContract}
                    handleSetActiveTab={handleChangeTab}
                    updateLandlord={updateLandlord}
                    updateLandlordLoading={updateLandlordLoading}
                    getLandlord={handleFetchLandlord}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.VERIFICATION}>
                  <LandlordVerification
                    landlord={landlord}
                    updateLandlordLoading={updateLandlordLoading}
                    getLandlord={handleFetchLandlord}
                    updateLandlord={updateLandlord}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.INVOICE_INFO}>
                  <LandlordInVoiceInfo
                    landlord={landlord}
                    getLandlord={handleFetchLandlord}
                    updateLandlord={updateLandlord}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.COMMISSION}>
                  <LandlordCommission
                    landlord={landlord}
                    getLandlord={handleFetchLandlord}
                    updateLandlord={updateLandlord}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.CONTACT}>
                  <LandlordContact
                    t={t}
                    landlord={landlord}
                    updateLandlord={updateLandlord}
                    setCurrentLandlord={setCurrentLandlord}
                    landlordNameList={landlordNameList}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.CONTRACT}>
                  <LandlordContract t={t} landlord={landlord} />
                </When>
                <When condition={activeTab === landlordDetailTabs.PROPERTY_RELATED}>
                  <LandlordPropertyRelated
                    t={t}
                    history={history}
                    properties={propertiesOfLandlord}
                    handleCreateProperty={gotoCreateProperty}
                    activePropertiesCount={propertiesOfLandlordCount}
                    getPropertiesByLandlordSlug={getPropertiesByLandlordSlug}
                    landlordSlug={landlord.slug}
                    isLoading={propertyListStatus === communicationStatus.FETCHING}
                    searchPropertyNameList={searchPropertyNameList}
                    getDisplayCountryList={getDisplayCountryList}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.PAYMENTPLAN}>
                  <PaymentDashboard t={t} landlord={landlord} />
                </When>
                <When condition={activeTab === landlordDetailTabs.BOOKINGS}>
                  <LandlordBookings
                    t={t}
                    history={history}
                    properties={propertiesOfLandlord}
                    handleCreateProperty={gotoCreateProperty}
                    activePropertiesCount={propertiesOfLandlordCount}
                    getPropertiesByLandlordSlug={getPropertiesByLandlordSlug}
                    landlordId={landlord.slug}
                    isLoading={propertyListStatus === communicationStatus.FETCHING}
                    getDisplayCountryList={getDisplayCountryList}
                  />
                </When>
                <When condition={activeTab === landlordDetailTabs.TOKEN}>
                  <LandlordToken
                    t={t}
                    history={history}
                    landlord={landlord}
                    createApiToken={createApiToken}
                    updateApiToken={updateApiToken}
                    accountInfo={accountInfo}
                    queryGetApiTokens={queryGetApiTokens}
                    roleId={roleId}
                    getLandlord={handleFetchLandlord}
                    handleGetAPITokens={handleGetAPITokens}
                    apiTokens={apiTokens}
                    updateLandlord={updateLandlord}
                  />
                </When>
              </Choose>
            </div>
          </DetailLayout>
        </When>
      </Choose>
    </div>
  );
};

export default authControl(platformEntity.LANDLORDS_LANDLORDS, entityAction.READ)(LandlordDetails);
